import React, { useEffect, useMemo, useState, useCallback } from "react";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../services/api";
import { endpoints } from "../../services/endpoints";
import { getUserDisplayName, getUserCompanyId, getUserDni } from "../../utils/user";

export default function GrantPadlockPermissionPage() {
  const { serialNumber } = useParams();
  const navigate = useNavigate();

  const [padlockId, setPadlockId] = useState(null);
  const [padlock, setPadlock] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userFilter, setUserFilter] = useState("");
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const [permissions, setPermissions] = useState(new Map()); 
  // Map userId -> { startDate, endDate, canOpen }

  const [canOpen, setCanOpen] = useState(true);
  const [startInput, setStartInput] = useState(dayjs().format("YYYY-MM-DDTHH:mm"));
  const [endInput, setEndInput] = useState(dayjs().add(7, "day").format("YYYY-MM-DDTHH:mm"));

  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState("");
  const [submitErr, setSubmitErr] = useState("");

  // ---------- resolver candado por serial ----------
  const resolvePadlock = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const rAll = await api.get(endpoints.padlock.list);
      const arr = rAll.data?.data ?? rAll.data ?? [];
      const wanted = String(serialNumber ?? "").trim();
      const found = Array.isArray(arr)
        ? arr.find((x) => String(x?.serialNumber ?? "").trim() === wanted)
        : null;

      if (!found?.id) throw new Error("No se pudo resolver el candado por serialNumber.");

      setPadlockId(found.id);

      try {
        const rDetail = await api.get(endpoints.padlock.byId(found.id));
        const detail = rDetail.data?.data ?? rDetail.data ?? found;
        setPadlock(detail);
      } catch {
        setPadlock(found);
      }
    } catch (e) {
      setErr(e);
    } finally {
      setLoading(false);
    }
  }, [serialNumber]);

  useEffect(() => {
    if (serialNumber) resolvePadlock();
  }, [serialNumber, resolvePadlock]);

  // companyId normalizado (del candado)
  const companyId = useMemo(() => {
    return (
      padlock?.company?.id ??
      padlock?.companyId ??
      padlock?.company_id ??
      null
    );
  }, [padlock]);

  // ---------- cargar usuarios de la misma empresa ----------
  const loadUsers = useCallback(async () => {
    if (!companyId) return;
    setUsersLoading(true);
    setSubmitErr("");
    setSubmitMsg("");
    try {
      const r = await api.get(endpoints.users.list);
      const raw = Array.isArray(r.data) ? r.data : (r.data?.data ?? []);
      const filtered = raw.filter((u) => getUserCompanyId(u) === companyId);

      const mapped = filtered.map((u) => ({
        id: u.id,
        name: getUserDisplayName(u),
        dni: getUserDni(u) ?? "—",
        email: u.email ?? u.mail ?? null,
      }));

      setUsers(mapped);
    } catch (e) {
      setSubmitErr(
        e?.response?.data?.message
          ? String(e.response.data.message)
          : String(e)
      );
    } finally {
      setUsersLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // ---------- cargar permisos existentes ----------
  const loadExistingPermissions = useCallback(async () => {
    if (!padlockId) return;
    try {
      const r = await api.get(endpoints.permissions.list, {
        params: { padlockId }
      });
      const perms = Array.isArray(r.data) ? r.data : (r.data?.data ?? []);
      const ids = new Set();
      const map = new Map();
      perms.forEach((p) => {
        ids.add(p.userId);
        map.set(p.userId, {
          startDate: p.startDate,
          endDate: p.endDate,
          canOpen: p.canOpen
        });
      });
      setSelectedUserIds(ids);
      setPermissions(map);
    } catch (e) {
      console.error("Error cargando permisos:", e);
    }
  }, [padlockId]);

  useEffect(() => {
    if (padlockId) {
      loadExistingPermissions();
    }
  }, [padlockId, loadExistingPermissions]);

  // ---------- UI helpers ----------
  function toggleUser(id) {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  // ---------- submit ----------
  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitErr("");
    setSubmitMsg("");

    if (!padlockId) { setSubmitErr("Candado no resuelto."); return; }
    if (selectedUserIds.size === 0) { setSubmitErr("Selecciona al menos un usuario."); return; }

    const startISO = dayjs(startInput).isValid() ? new Date(startInput).toISOString() : null;
    const endISO   = dayjs(endInput).isValid()   ? new Date(endInput).toISOString()   : null;
    if (!startISO || !endISO) { setSubmitErr("Fechas inválidas."); return; }

    setSubmitting(true);
    try {
      let ok = 0;
      for (const uid of Array.from(selectedUserIds)) {
        try {
          await api.post(endpoints.permissions.create, {
            padlockId,
            userId: uid,
            canOpen: !!canOpen,
            startDate: startISO,
            endDate: endISO,
          });
          ok++;
        } catch (err) {
          console.error("Error asignando permiso:", err);
        }
      }

      if (ok > 0) {
        navigate(`/padlocks/${encodeURIComponent(serialNumber)}`);
      } else {
        setSubmitErr("No se pudo asignar permisos a ningún usuario.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  const filteredUsers = useMemo(() => {
    const q = userFilter.trim().toLowerCase();
    if (!q) return users;
    const hay = (x) => (typeof x === "string" ? x.toLowerCase() : "");
    return users.filter(
      (u) => hay(u.name).includes(q) || hay(u.dni).includes(q) || hay(u.email).includes(q)
    );
  }, [users, userFilter]);

  // ---------- render ----------
  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Permisos de apertura</h1>
        <button className="px-3 py-2 rounded border" onClick={() => navigate(-1)}>
          Volver
        </button>
      </div>

      {loading && <div className="text-gray-500">Cargando candado…</div>}
      {err && (
        <div className="p-3 rounded bg-red-50 text-red-700 border border-red-200">
          {String(err?.response?.data?.message ?? err)}
        </div>
      )}

      {!loading && !err && (
        <>
          <div className="bg-white p-4 rounded shadow">
            <div className="text-sm text-gray-500">Candado</div>
            <div className="text-lg font-semibold">
              {padlock?.name ?? "—"}{" "}
              <span className="text-gray-500">({padlock?.serialNumber ?? serialNumber})</span>
            </div>
            <div className="text-xs text-gray-400">
              Compañía: {padlock?.company?.name ?? "—"}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="bg-white p-4 rounded shadow space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Buscar usuario</label>
                <input
                  type="text"
                  value={userFilter}
                  onChange={(e) => setUserFilter(e.target.value)}
                  className="w-full border rounded px-3 py-2"
                  placeholder="Nombre, Identificador o email…"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Usuarios de {padlock?.company?.name ?? "—"} ({users.length})
                </label>
                <div className="max-h-72 overflow-auto border rounded">
                  {usersLoading ? (
                    <div className="p-3 text-gray-500">Cargando usuarios…</div>
                  ) : filteredUsers.length === 0 ? (
                    <div className="p-3 text-gray-500">No hay usuarios.</div>
                  ) : (
                    filteredUsers.map((u) => {
                      const checked = selectedUserIds.has(u.id);
                      const perm = permissions.get(u.id);
                      return (
                        <label
                          key={u.id}
                          className="flex items-center gap-2 px-3 py-2 border-b last:border-b-0 cursor-pointer hover:bg-gray-50"
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleUser(u.id)}
                          />
                          <div className="flex flex-col">
                            <span className="font-medium">{u.name}</span>
                            <span className="text-xs text-gray-500">
                              Identificador: {u.dni} {u.email ? `· ${u.email}` : ""}
                            </span>
                            {perm && (
                              <span className="text-xs text-green-600">
                                Permiso: {dayjs(perm.startDate).format("DD/MM/YYYY HH:mm")} → {dayjs(perm.endDate).format("DD/MM/YYYY HH:mm")}
                              </span>
                            )}
                          </div>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white p-4 rounded shadow grid grid-cols-1 md:grid-cols-3 gap-3">
              <label className="block">
                <span className="text-sm font-medium">Desde</span>
                <input
                  type="datetime-local"
                  className="mt-1 w-full border rounded px-3 py-2"
                  value={startInput}
                  onChange={(e) => setStartInput(e.target.value)}
                />
              </label>
              <label className="block">
                <span className="text-sm font-medium">Hasta</span>
                <input
                  type="datetime-local"
                  className="mt-1 w-full border rounded px-3 py-2"
                  value={endInput}
                  onChange={(e) => setEndInput(e.target.value)}
                />
              </label>
              <label className="inline-flex items-center gap-2 mt-6 md:mt-auto">
                <input
                  type="checkbox"
                  checked={canOpen}
                  onChange={(e) => setCanOpen(e.target.checked)}
                />
                <span className="text-sm font-medium">Puede abrir</span>
              </label>
            </div>

            {submitErr && (
              <div className="p-3 rounded bg-red-50 text-red-700 border border-red-200">
                {submitErr}
              </div>
            )}
            {submitMsg && (
              <div className="p-3 rounded bg-green-50 text-green-700 border border-green-200">
                {submitMsg}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="px-3 py-2 rounded border"
                onClick={() => navigate(-1)}
              >
                Cancelar
              </button>
              <div className="flex flex-col items-end">
                <button
                  type="submit"
                  disabled={true} // deshabilitado temporalmente
                  className="px-3 py-2 rounded border text-white bg-gray-400 cursor-not-allowed"
                >
                  Guardar permisos
                </button>
                <span className="text-xs text-red-600 mt-1">
                  Temporalmente inactivado por pruebas
                </span>
              </div>
          </div>
          </form>
        </>
      )}
    </div>
  );
}